import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

/* ── toasts ─────────────────────────────────────────────── */

interface Toast {
  id: number;
  message: string;
  kind: 'ok' | 'err';
}

const ToastContext = createContext<{ push: (message: string, kind?: 'ok' | 'err') => void } | null>(
  null,
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, kind: 'ok' | 'err' = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, kind }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 4500);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <output className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.kind === 'ok' ? 'toast-ok' : 'toast-err'}`}>
            <span>{toast.message}</span>
          </div>
        ))}
      </output>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside ToastProvider');
  return value;
}

/* ── modal ──────────────────────────────────────────────── */

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  // A native dialog brings the focus trap, Escape handling and inert background with it,
  // rather than us reimplementing all three on a div.
  useEffect(() => {
    const element = ref.current;
    if (element && !element.open) element.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-label={title}
      onClose={onClose}
      onCancel={onClose}
      onMouseDown={(event) => {
        // Clicks on the backdrop report the dialog itself as the target.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="card modal">
        <div className="card-head">
          <h3>{title}</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">{footer}</div>
      </div>
    </dialog>
  );
}

/* ── small pieces ───────────────────────────────────────── */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    // The control arrives as children and is wrapped by this label, which is valid implicit
    // association. Groups of buttons use FieldGroup instead, which is a real fieldset.
    // biome-ignore lint/a11y/noLabelWithoutControl: implicit association through children
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/** Same look as Field, for a set of buttons — a <label> may only wrap a form control. */
export function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="field">
      <legend className="label">{label}</legend>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </fieldset>
  );
}

export function Spinner() {
  return <span className="spin" aria-label="Loading" />;
}

export function Loading() {
  return (
    <div className="center-pad">
      <Spinner />
    </div>
  );
}

export function Empty({ mark, title, hint }: { mark: string; title: string; hint?: string }) {
  return (
    <div className="empty">
      <span className="empty-mark" aria-hidden="true">
        {mark}
      </span>
      <strong>{title}</strong>
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function StatusPill({ published }: { published: boolean }) {
  return (
    <span className={`pill ${published ? 'pill-live' : 'pill-draft'}`}>
      <span className="dot" />
      {published ? 'Live' : 'Draft'}
    </span>
  );
}

/** Seconds as m:ss — story lengths are always minutes, never hours. */
export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
